import {
  Language,
  BreadcrumbNavigationItem,
  Theme,
  CountryDto,
  NLH,
  CoachList,
  EatingHabitDto,
  FoodItemDto,
  MedicationDto,
  IllnessDto,
  LanguageDto,
  AccountPreferences,
} from '@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { GlobalConfig } from 'vuestic-ui'
import { getAccountPreferences, updateAccountPreferences } from '@/api'
import { supportedLanguages } from '@/i18n/constants'

export const useGlobalStore = defineStore('global', () => {
  // State for language, theme, and minimized sidebar
  const isSidebarMinimized = ref<boolean>(false)
  const language = ref<Language>('en')
  const languagesStatic = ref([
    { id: 1, name: 'Deutsch', code: 'de' },
    { id: 2, name: 'English USA', code: 'en-us' },
    { id: 3, name: 'Spanish E', code: 'es' },
    { id: 4, name: 'Spanish USA', code: '4' },
    { id: 5, name: 'Deutsch Test', code: '5' },
    { id: 6, name: 'Englisch GB', code: 'en-gb' },
    { id: 7, name: 'Deutsch CH', code: 'de-ch' },
    { id: 8, name: 'Polish', code: 'pl' },
    { id: 9, name: 'Hungarian', code: 'hu' },
    { id: 10, name: 'Russisch-Russland', code: 'ru' },
    { id: 11, name: 'Italian', code: 'it' },
    { id: 12, name: 'France', code: 'fr' },
    { id: 13, name: 'Deutsch A', code: 'de-at' },
    { id: 17, name: 'Dutch', code: 'nl' },
    { id: 18, name: 'Turkey', code: 'tr' },
    { id: 19, name: 'English Asia', code: 'en-ph' },
    { id: 31, name: 'Chinese', code: 'zh' },
    { id: 32, name: 'Griechisch', code: 'el' },
    { id: 36, name: 'Czech', code: 'cs' },
    { id: 38, name: 'Romanian', code: 'ro' },
    { id: 39, name: 'Estonian', code: 'et' },
    { id: 40, name: 'Mexican', code: 'es-mx' },
    { id: 46, name: 'Spanisch-Kolumbien', code: 'es-co' },
    { id: 48, name: 'Argentinisch', code: 'es-ar' },
    { id: 49, name: 'Slovakian', code: 'sk' },
    { id: 50, name: 'Bulgarian', code: 'bg' },
    { id: 62, name: 'Croatian', code: 'hr' },
    { id: 63, name: 'English CAN', code: 'en-ca' },
    { id: 64, name: 'Persisch (Farsi)', code: 'fa' },
    { id: 65, name: 'Englisch AU', code: 'en-au' },
    { id: 66, name: 'Englisch India', code: 'en-in' },
    { id: 67, name: 'France CAN', code: 'fr-ca' },
    { id: 68, name: 'Slowenisch', code: 'sl' },
    { id: 69, name: 'English Oman', code: 'en-om' },
    { id: 70, name: 'Portuges (Brazil)', code: 'pt-br' },
    { id: 71, name: 'English International', code: 'en' },
    { id: 72, name: 'Englisch ZA', code: 'en-za' },
    { id: 73, name: 'Englisch Kenia', code: 'en-ke' },
    { id: 74, name: 'Ukrainisch', code: 'uk' },
    { id: 75, name: 'Spanisch-Peruanisch', code: 'es-pe' },
    { id: 76, name: 'Vietnamesisch', code: 'vi' },
    { id: 77, name: 'Russisch-Ukraine/Baltikum', code: 'ru-ua' },
    { id: 78, name: 'Portugiesisch-Portugal', code: 'pt' },
    { id: 79, name: 'Russisch-Turkstaaten', code: 'ru-kg' },
    { id: 80, name: 'Italian CH', code: 'it-ch' },
    { id: 81, name: 'French CH', code: 'fr-ch' },
    { id: 82, name: 'Arabisch Oman', code: 'ar-om' },
    { id: 257, name: 'Sport (Deutsch)', code: '257' },
    { id: 258, name: 'Sport (English - US)', code: '258' },
    { id: 268, name: 'Sport (Français)', code: '268' },
    { id: 769, name: 'LCR (Deutsch)', code: '769' },
  ])
  const theme = ref<Theme>((localStorage.getItem('theme') as Theme) || 'light')
  const breadCrumbs = ref<BreadcrumbNavigationItem[]>([])

  const countries = ref<CountryDto[]>([])
  const nlhs = ref<NLH[]>([])
  const coaches = ref<CoachList[]>([])
  const eatingHabits = ref<EatingHabitDto[]>([])
  const foods = ref<FoodItemDto[]>([])
  const medications = ref<MedicationDto[]>([])
  const illnesses = ref<IllnessDto[]>([])
  const languages = ref<LanguageDto[]>([])
  const accountPreferenceLanguage = ref<Language>()
  const languageLoaded = ref<boolean>(false)
  const languageId = ref<number | undefined>(undefined)
  const activeRequests = ref(0)
  const isLoading = computed(() => activeRequests.value > 0)

  // Method for toggling sidebar minimization
  const toggleSidebar = () => {
    isSidebarMinimized.value = !isSidebarMinimized.value
  }

  // Method for changing the theme
  const setTheme = (themeName: Theme, vaColorConfig: GlobalConfig['colors']) => {
    theme.value = themeName
    localStorage.setItem('theme', themeName)
    vaColorConfig.currentPresetName = themeName // Apply the theme using Vuestic
  }

  // Method to load Language
  let loadLanguagePromise: Promise<void> | null = null
  const loadLanguage = async () => {
    if (loadLanguagePromise) return loadLanguagePromise

    loadLanguagePromise = (async () => {
      const langFromApi = (await getAccountPreferences()).language
      accountPreferenceLanguage.value = langFromApi
      const lang = supportedLanguages.includes(langFromApi) ? langFromApi : 'en'
      language.value = lang
      if (langFromApi !== localStorage.getItem('language')) {
        languageLoaded.value = false
      }
      localStorage.setItem('language', lang)
    })()

    try {
      await loadLanguagePromise
    } finally {
      loadLanguagePromise = null
    }
  }

  // Method for Language save
  const setLanguage = async (lang: Language) => {
    language.value = (await updateAccountPreferences(new AccountPreferences({ language: lang }))).language
    countries.value.length = 0
    localStorage.setItem('language', lang)
  }

  const setBreadCrumbs = (links: BreadcrumbNavigationItem[]) => {
    breadCrumbs.value = [...links]
  }

  const startLoading = () => {
    activeRequests.value++
  }

  const stopLoading = () => {
    activeRequests.value--
    if (activeRequests.value < 0) {
      activeRequests.value = 0 // safeguard
    }
  }

  const setCountries = (data: CountryDto[]) => {
    countries.value = [...data]
  }

  const getLanguageById = (id: number) => {
    return languagesStatic.value.find((language) => language.id === id)
  }

  const setNLHs = (data: NLH[]) => {
    nlhs.value = [...data]
  }
  const setCoaches = (data: CoachList[]) => {
    coaches.value = [...data]
  }
  const setEatingHabits = (data: EatingHabitDto[]) => {
    eatingHabits.value = [...data]
  }
  const setFoods = (data: FoodItemDto[]) => {
    foods.value = [...data]
  }
  const setFoodLanguageId = (language: number | undefined) => {
    languageId.value = language
  }
  const setMedications = (data: MedicationDto[]) => {
    medications.value = [...data]
  }
  const setlllnesses = (data: IllnessDto[]) => {
    illnesses.value = [...data]
  }
  const setLanguages = (data: LanguageDto[]) => {
    languages.value = [...data]
  }

  const clearGlobalStore = () => {
    countries.value = []
    nlhs.value = []
    coaches.value = []
    eatingHabits.value = []
    foods.value = []
    medications.value = []
    illnesses.value = []
    languages.value = []
  }

  return {
    isSidebarMinimized,
    language,
    accountPreferenceLanguage,
    languageLoaded,
    theme,
    breadCrumbs,
    isLoading,
    countries,
    nlhs,
    coaches,
    eatingHabits,
    foods,
    medications,
    illnesses,
    languages,
    languageId,
    activeRequests,
    toggleSidebar,
    loadLanguage,
    setLanguage,
    setTheme,
    setBreadCrumbs,
    startLoading,
    stopLoading,
    setCountries,
    getLanguageById,
    setNLHs,
    setCoaches,
    setEatingHabits,
    setFoods,
    clearGlobalStore,
    setMedications,
    setlllnesses,
    setLanguages,
    setFoodLanguageId,
  }
})
